import { useState, useEffect } from "react";
import {
  Button,
  Card,
  CardContent,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Divider,
  Modal,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Theme,
  createStyles,
} from "@mui/material";
import { Header } from "../Header";
import {
  Container,
  Content,
  IconWrapper,
  StyledButtonDelete,
  StyledButtonEdit,
  StyledDate,
  StyledDividerRow,
  StyledDividerTd,
  StyledSendButton,
  StyledTd,
  StyledTdActions,
  StyledTh,
  StyledTr,
  TableHeader,
  Title,
} from "./style";
import { Loading } from "../Loading";
import { getSessionUser } from "../../utils/users";
import { IUser } from "../Login/ILogin";
import { apiOperation, headers } from "../../services/api";
import { Paginacao, Resultado } from "../../models/base/basic";
import { Error } from "../Error";
import React from "react";

let usuarioLogado: IUser | null = null;

const emptyGuid = "00000000-0000-0000-0000-000000000000";

export interface EmpresaIdentificacao {
  id: string;
  razao: string;
}

export interface EmpresaUnidade {
  empresaUnidadeId: string;
  empresaId: string;
  empresaNome?: string; 
  descricao: string;
  status: string; 
  usuarioInclusao: string;
  dataInclusao: Date;
  dataEdicao: Date;
  usuarioEdicao: string;
}

export interface Empresa {
  empresaId: string;
  nome: string;
}

const editInicial: EmpresaUnidade = {
  empresaUnidadeId: emptyGuid,
  empresaId: emptyGuid,
  empresaNome: "",
  descricao: "",
  status: "Ativo",
  usuarioInclusao: "",
  dataInclusao: new Date(),
  dataEdicao: new Date(),
  usuarioEdicao: "",
};

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  borderRadius: "4px",
};

export const Unit = () => {
  const [loadingModal, setLoadingModal] = useState<boolean>(false);
  const [messagesLoading, setMessagesLoading] = useState<string>("");
  const [unitList, setUnitList] = useState<EmpresaUnidade[]>([]); 
  const [edicaoId, setEdicaoId] = useState<string | null>("");
  const [edicao, setEdicao] = useState<EmpresaUnidade>(editInicial);
  const [open, setOpen] = useState(false);
  const [isValid, setIsValid] = useState<boolean | null>(null);
  const [documentFormat, setDocumentFormat] = useState<string | null>(null);
  const [errorApi, setErrorApi] = useState({
    message: "",
    title: "",
    state: false,
  });
  const [empresas, setEmpresas] = useState<EmpresaIdentificacao[]>([]);

  useEffect(() => {
    usuarioLogado = getSessionUser();
    carregarUnitList();
    carregarEmpresaList(); // Carregar lista de empresas
  }, []);

  async function carregarUnitList() {
    setLoadingModal(true);
    try {
      const urlString = `EmpresaUnidade`; 
      const config = {
        headers: headers(),
      };
      const { data } = await apiOperation.get<EmpresaUnidade[]>(urlString, config);
      setUnitList(data);
    } catch (error: any) {
      setErrorApi({
        title: `API Não disponível`,
        message: `Falha ao buscar unidades!`, 
        state: true,
      });
    } finally {
      setLoadingModal(false);
    }
  }

  async function carregarEmpresaList() {
    try {
      const urlString = `empresaunidade/empresas`; 
      const config = {
        headers: headers(),
      };
      const { data } = await apiOperation.get<EmpresaIdentificacao[]>(urlString, config);
      setEmpresas(data);
    } catch (error: any) {
      console.error("Falha ao buscar empresas:", error);
    }
  }

  function cadastrar() {
    const newItem = structuredClone(editInicial);
    editar(newItem);
  }

  async function excluir(id: string) {
    try {
      setLoadingModal(true);
      const urlString = `EmpresaUnidade/${id}`;
      const config = {
        headers: headers(),
      };
      await apiOperation.delete(urlString, config);
    } catch (error: any) {
      setErrorApi({
        title: `API Não disponível`,
        message: `Falha ao excluir!`,
        state: true,
      });
    } finally {
      carregarUnitList();
      setLoadingModal(false);
    }
  }

  function editar(valor: EmpresaUnidade) {
    setOpen(true);
    setEdicao(valor);
    setEdicaoId(valor.empresaUnidadeId);

    setIsValid(null);
    setDocumentFormat(null);
  }

  function fecharModal() {
    setOpen(false);
    if (edicao.empresaUnidadeId === emptyGuid && !edicao.descricao) {
      setUnitList(unitList.filter((item) => item.empresaUnidadeId !== edicaoId));
    }
  }

  function cancelar() {
    setOpen(false);
    if (edicao.empresaUnidadeId === emptyGuid) { 
      setUnitList(unitList.filter((item) => item.empresaUnidadeId !== edicaoId));
    }

    setEdicaoId(null);
    setEdicao(editInicial);
  }

  async function salvar() {
    try {
      setLoadingModal(true);
      const urlString = `EmpresaUnidade`; 
      const config = {
        headers: headers(),
      };

      let data: Resultado<boolean> | undefined = undefined;

      if (edicao.empresaUnidadeId === emptyGuid) {
        const response = await apiOperation.post<Resultado<boolean>>(
          urlString,
          edicao,
          config
        );
        data = response.data;
      } else {
        const response = await apiOperation.put<Resultado<boolean>>(
          urlString,
          edicao,
          config
        );
        data = response.data;
      }

      if (data.erro) {
        const erros = data.erros.map((erro) => erro).join(", ");
        setErrorApi({
          title: `Erro ao salvar`,
          message: erros,
          state: true,
        });
      }

      setOpen(false);
    } catch (error: any) {
      setErrorApi({
        title: `API Não disponível`,
        message: `Falha ao salvar unidade!`,
        state: true,
      });
    } finally {
      carregarUnitList();
      setLoadingModal(false);
    }
    setEdicaoId(null);
  }

  function handleDocumentChange(value: string) {
    setEdicao({ ...edicao, descricao: value });
    
    setIsValid(true);
    setDocumentFormat("Descrição");
  }

  function handleEmpresaChange(id: string) {
    const empresaSelecionada = empresas.find(emp => emp.id === id);
    setEdicao({ 
      ...edicao, 
      empresaId: empresaSelecionada?.id || "",
      empresaNome: empresaSelecionada?.razao || ""
    });
  }

  function mapList(list: EmpresaUnidade[]) {
    return list.map((item, index) => {
      const showDivider = index !== list.length - 1;

      return (
        <React.Fragment key={item.empresaUnidadeId}>
          <StyledTr>
            <StyledTd>{item.empresaNome || "N/A"}</StyledTd>
            <StyledTd>{item.descricao}</StyledTd>
            <StyledTd>{item.status}</StyledTd>
            <StyledTd>{item.usuarioInclusao}</StyledTd>
            <StyledTd>
              <StyledDate>
                {new Date(item.dataEdicao).toLocaleString()}
              </StyledDate>
            </StyledTd>
            <StyledTdActions>
              <StyledButtonEdit
                onClick={() => editar(item)} 
                variant="contained"
                color="primary"
              >
                Editar
              </StyledButtonEdit>

              <StyledButtonDelete
                onClick={() => excluir(item.empresaUnidadeId)}
                variant="contained"
                color="error"
              >
                Excluir
              </StyledButtonDelete>
            </StyledTdActions>
          </StyledTr>

          {showDivider && (
            <StyledDividerRow>
              <StyledDividerTd colSpan={6}>
                <Divider />
              </StyledDividerTd>
            </StyledDividerRow>
          )}
        </React.Fragment>
      );
    });
  }

  return (
    <>
      <Modal open={open} onClose={fecharModal} aria-labelledby="modal-title">
        <Box sx={modalStyle}>
          <Typography
            id="modal-title"
            variant="h6"
            component="h2"
            sx={{ mb: 2 }}
          >
            {edicao.empresaUnidadeId === emptyGuid
              ? "Cadastrar Unidade"
              : "Editar Unidade"}
          </Typography>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Empresa
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <Select
              value={edicao.empresaId}
              onChange={(e) => handleEmpresaChange(e.target.value)}
              displayEmpty
              disabled={false}
            >
              {empresas.map((empresa) => (
                <MenuItem key={empresa.id} value={empresa.id}>
                  {empresa.razao}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography
            variant="subtitle2"
            sx={{ mb: 1 }}
          >
            Unidade
          </Typography>

          <TextField
            fullWidth
            placeholder="Unidade"
            value={edicao.descricao}
            onChange={(e) => handleDocumentChange(e.target.value)}
            error={false}
            helperText=""
            disabled={false}
            sx={{ mb: 1 }}
          />

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Status
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <Select
              value={edicao.status}
              onChange={(e) => setEdicao({ ...edicao, status: e.target.value })}
              displayEmpty
              disabled={false}
            >
              <MenuItem value="Ativo">Ativo</MenuItem>
              <MenuItem value="Inativo">Inativo</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ display: "flex", justifyContent: "flex-start", gap: 1 }}>
            <StyledSendButton
              variant="contained"
              onClick={salvar}
              disabled={false} 
            >
              <IconWrapper>▶</IconWrapper> SALVAR
            </StyledSendButton>
            
            <Button
              variant="outlined"
              onClick={cancelar}
            >
              CANCELAR
            </Button>
          </Box>
        </Box>
      </Modal>

      <Header />
      <Loading
        mode="blurry"
        hidden={!loadingModal}
        messages={messagesLoading}
      />
      <Container>
        <Content>
          <Title>Unidades</Title>
          <Button
            onClick={cadastrar}
            className={"buttonPesquisa"}
            variant="contained"
            color="secondary"
            sx={{
              width: "10.4em",
            }}
          >
            Cadastrar
          </Button>
        </Content>

        <Card sx={{ minWidth: 275, placeItems: "center" }}>
          <table
            style={{
              width: "100%",
              height: "100%",
              padding: "10px",
            }}
          >
            <TableHeader>
              <tr>
                <StyledTh>Empresa</StyledTh>
                <StyledTh>Unidade</StyledTh>
                <StyledTh>Status</StyledTh>
                <StyledTh>Usuário Inclusão</StyledTh>
                <StyledTh>Data</StyledTh>
                <StyledTh>Ações</StyledTh>
              </tr>
            </TableHeader>
            <tbody>{mapList(unitList)}</tbody> 
          </table>
        </Card>
        <Error
          hasOpen={errorApi.state}
          handleClose={() => setErrorApi({ ...errorApi, state: false })}
          errorMsg={errorApi.message}
          title={errorApi.title}
        />
      </Container>
    </>
  );
};